import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';

import {
  QUESTION_GROUP_LOGIC,
  QUESTION_GROUP_LOGIC_BRANCHES,
  QUESTION_LOGIC_OPERATORS,
} from '@api/instant_survey';
import {
  ActionOverlayPopover,
  Button,
  Card,
  ErrorText,
  FlowArrowIcon,
  Layout,
  ModalV2 as Modal,
  OverlayTrigger,
  TargetIcon,
  Tooltip,
} from '@utilities';

import QuestionGroupLogic from './components/QuestionGroupLogic';
import {
  getNonInstructionQuestionCountBefore,
  getQuestionLogicSourceQuestions,
} from '../../../../../../../../utilities/helpers';
import { collectGroupSubtree } from '../../../../../../utilities/helpers';

import styles from './_index.module.scss';

const BRANCH_ACTIONS = [
  { branch: 'skip', icon: FlowArrowIcon, label: 'Display logic' },
  { branch: 'disqualify', icon: TargetIcon, label: 'Disqualify panelists' },
];

const DISQUALIFY_POSITION_TOOLTIP =
  'Disqualification can only be applied to a question group that starts within the first two ' +
  'questions of the survey.';

const ModalQuestionGroupLogic = ({
  onClose,
  questionGroups,
  questionLogic,
  questions,
  setQuestionLogic,
}) => {
  const { permissions = {} } = useSelector((state) => state?.user?.data) || {};
  const { canAdvancedEditSurvey } = permissions;

  const [localQuestionLogic, setLocalQuestionLogic] = useState(cloneDeep(questionLogic));
  const skipOperators = [QUESTION_LOGIC_OPERATORS.ALL, QUESTION_LOGIC_OPERATORS.ANY];
  const firstErrorRef = useRef(null);
  const [activePopoverGroupId, setActivePopoverGroupId] = useState(null);

  const groupSourceQuestions = useMemo(() => {
    const map = {};
    questionGroups.forEach((group) => {
      const firstGroupMember = questions.find(
        (question) => String(question.questionGroup) === String(group.id)
      );
      const excludedGroupIds = new Set([String(group.id)]);
      questionGroups.forEach((candidate) => {
        if (String(candidate.parentGroup) === String(group.id)) {
          excludedGroupIds.add(String(candidate.id));
        }
      });
      map[group.id] = firstGroupMember
        ? getQuestionLogicSourceQuestions({ currentQuestion: firstGroupMember, questions }).filter(
            (question) => !excludedGroupIds.has(String(question.questionGroup))
          )
        : [];
    });
    return map;
  }, [questionGroups, questions]);

  const orderedQuestionGroups = useMemo(() => {
    const firstIndexById = {};
    questions.forEach((question, index) => {
      const groupId = question.questionGroup;
      if (groupId != null && firstIndexById[groupId] === undefined) {
        firstIndexById[groupId] = index;
      }
    });
    // Display groups in questionnaire order rather than the group id
    return [...questionGroups].sort(
      (a, b) => (firstIndexById[a.id] ?? Infinity) - (firstIndexById[b.id] ?? Infinity)
    );
  }, [questionGroups, questions]);

  // A group may only disqualify when it starts within the first two non-instruction questions.
  // Its start is the first question anywhere in its subtree: a parent group can hold nothing but
  // child groups, in which case it has no direct members of its own.
  const canDisqualifyByGroupId = useMemo(() => {
    const map = {};
    questionGroups.forEach((group) => {
      const subtreeGroupIds = new Set(
        collectGroupSubtree({ group, questionGroups }).map(({ id }) => String(id))
      );
      const firstMemberIndex = questions.findIndex((question) =>
        subtreeGroupIds.has(String(question.questionGroup))
      );
      map[String(group.id)] =
        Boolean(canAdvancedEditSurvey) ||
        (firstMemberIndex !== -1 &&
          getNonInstructionQuestionCountBefore(questions, firstMemberIndex) < 2);
    });
    return map;
  }, [canAdvancedEditSurvey, questionGroups, questions]);

  const getInitialExpandedGroups = () => {
    const expanded = new Set();
    Object.keys(questionLogic[QUESTION_GROUP_LOGIC] || {}).forEach((groupId) => {
      expanded.add(String(groupId));
    });
    return expanded;
  };
  const [expandedGroupIds, setExpandedGroupIds] = useState(getInitialExpandedGroups);

  const getGroupBranches = (groupId) => localQuestionLogic[QUESTION_GROUP_LOGIC]?.[groupId] || {};

  const hasBranch = (groupId, branch) => Boolean(getGroupBranches(groupId)[branch]);

  const hasGroupLogicErrors = (groupId) =>
    QUESTION_GROUP_LOGIC_BRANCHES.some((branch) =>
      (getGroupBranches(groupId)[branch]?.logic || []).some(
        (row) => row.hasAnswerError || row.hasQuestionError
      )
    );

  const isAllowedSourceQuestion = (groupId, sourceQuestion) =>
    (groupSourceQuestions[groupId] || []).some(
      ({ id }) => String(id) === String(sourceQuestion?.id)
    );

  const handleSave = () => {
    const modifiedQuestionLogic = cloneDeep(localQuestionLogic);
    let isValid = true;

    const groupLogicMap = modifiedQuestionLogic[QUESTION_GROUP_LOGIC] || {};
    Object.keys(groupLogicMap).forEach((groupId) => {
      QUESTION_GROUP_LOGIC_BRANCHES.forEach((branch) => {
        const groupLogic = groupLogicMap[groupId]?.[branch];
        if (!groupLogic?.logic) return;

        groupLogic.logic.forEach((row) => {
          // For quota group logic
          if (Object.prototype.hasOwnProperty.call(row, 'url_variable')) {
            if (!row.values || row.values.length === 0) {
              row.hasAnswerError = true;
              row.isFirstAnswerError = isValid;
              isValid = false;
            } else {
              row.hasAnswerError = false;
              row.isFirstAnswerError = false;
            }
          }
          // For answer-based logic
          else {
            if (!row.question?.id || !isAllowedSourceQuestion(groupId, row.question)) {
              row.hasQuestionError = true;
              row.isFirstQuestionError = isValid;
              isValid = false;
            } else {
              row.hasQuestionError = false;
              row.isFirstQuestionError = false;
            }

            if (!row.answers || row.answers.length === 0) {
              row.hasAnswerError = true;
              row.isFirstAnswerError = isValid;
              isValid = false;
            } else {
              row.hasAnswerError = false;
              row.isFirstAnswerError = false;
            }
          }
        });
      });
    });

    if (!isValid) {
      setLocalQuestionLogic(modifiedQuestionLogic);
      setTimeout(() => {
        firstErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }

    setQuestionLogic(modifiedQuestionLogic);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const addGroupLogicBranch = (rawGroupId, branch) => {
    const groupId = String(rawGroupId);
    const updatedLogic = cloneDeep(localQuestionLogic);
    const existingGroupLogic = updatedLogic[QUESTION_GROUP_LOGIC] || {};
    // With no eligible source question ahead of the group, only a quota-group condition can
    // ever be authored, so seed the row as one rather than an unfillable answer row.
    const initialRow =
      (groupSourceQuestions[groupId] || []).length === 0
        ? {
            operator: QUESTION_LOGIC_OPERATORS.ANY,
            url_variable: 'instant_survey_group_ref',
            values: [],
          }
        : {
            answers: [],
            operator: QUESTION_LOGIC_OPERATORS.ANY,
            question: null,
          };

    updatedLogic[QUESTION_GROUP_LOGIC] = {
      ...existingGroupLogic,
      [groupId]: {
        ...(existingGroupLogic[groupId] || {}),
        [branch]: {
          logic: [initialRow],
          // Matches the per-question defaults: skip seeds ALL, disqualify seeds ANY.
          operator: branch === 'disqualify' ? QUESTION_LOGIC_OPERATORS.ANY : skipOperators[0],
        },
      },
    };

    setLocalQuestionLogic(updatedLogic);
    setExpandedGroupIds((prev) => new Set(prev).add(groupId));
  };

  const removeGroupLogicBranch = (rawGroupId, branch) => {
    const groupId = String(rawGroupId);
    const updatedLogic = cloneDeep(localQuestionLogic);
    const group = updatedLogic[QUESTION_GROUP_LOGIC]?.[groupId];

    if (group?.[branch]) {
      delete group[branch];

      // A group with no branches left loses its entry, and the reserved key goes with the
      // last group so an empty object is never sent to the API.
      if (Object.keys(group).length === 0) {
        delete updatedLogic[QUESTION_GROUP_LOGIC][groupId];
        setExpandedGroupIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(groupId);
          return newSet;
        });
      }

      if (Object.keys(updatedLogic[QUESTION_GROUP_LOGIC]).length === 0) {
        delete updatedLogic[QUESTION_GROUP_LOGIC];
      }

      setLocalQuestionLogic(updatedLogic);
    }
  };

  const renderBranchAction = (group, { branch, icon: BranchIcon, label }) => {
    const groupId = String(group.id);
    const isApplied = hasBranch(groupId, branch);
    // Only adding is gated. A branch authored before the group moved must stay removable,
    // otherwise Save would keep persisting a rule the author cannot reach.
    const isDisqualifyBlocked =
      !isApplied && branch === 'disqualify' && !canDisqualifyByGroupId[groupId];
    const actionLabel = isApplied ? `Remove ${label.toLowerCase()}` : label;

    return (
      <li key={`${groupId}-${branch}-action`}>
        <OverlayTrigger.Tooltip
          overlay={
            isDisqualifyBlocked ? (
              <Tooltip className={styles['modal-question-group-logic-action-tooltip']}>
                {DISQUALIFY_POSITION_TOOLTIP}
              </Tooltip>
            ) : (
              <></>
            )
          }
          placement="bottom"
        >
          <button
            aria-label={actionLabel}
            data-testid={`question-group-${branch}-button-${groupId}`}
            disabled={isDisqualifyBlocked}
            onClick={() => {
              if (isApplied) {
                removeGroupLogicBranch(groupId, branch);
              } else {
                addGroupLogicBranch(groupId, branch);
              }
              setActivePopoverGroupId(null);
            }}
            type="button"
          >
            <BranchIcon />
            {actionLabel}
          </button>
        </OverlayTrigger.Tooltip>
      </li>
    );
  };

  return (
    <Modal
      buttons={[
        <Button key="cancel" onClick={handleCancel} text="Cancel" variant="secondary" />,
        <Button key="save" onClick={handleSave} text="Save" />,
      ]}
      className={styles['modal-question-group-logic']}
      enforceFocus={false}
      isActive
      onClose={handleCancel}
      size="large"
      title="Question Group Logic"
    >
      <Layout.Flex.Column gap="medium">
        <p>
          By default, all question groups are displayed and no question group disqualifies
          panelists.
        </p>
        <div className={styles['modal-question-group-logic-list']}>
          {orderedQuestionGroups.map((group) => {
            const groupId = String(group.id);
            const isExpanded = expandedGroupIds.has(groupId);
            return (
              <Card className={styles['modal-question-group-logic-card']} key={group.id}>
                <div className={styles['modal-question-group-logic-group']}>
                  <div className={styles['modal-question-group-logic-group-text']}>
                    {group.name}
                  </div>
                  <ActionOverlayPopover
                    aria-label={`Logic actions for ${group.name}`}
                    hasArrow={false}
                    isActive={activePopoverGroupId === groupId}
                    onClick={() =>
                      setActivePopoverGroupId(activePopoverGroupId === groupId ? null : groupId)
                    }
                    onHide={() => setActivePopoverGroupId(null)}
                  >
                    <ul>{BRANCH_ACTIONS.map((action) => renderBranchAction(group, action))}</ul>
                  </ActionOverlayPopover>
                </div>
                {isExpanded && (
                  <div className={styles['modal-question-group-logic-branches']}>
                    {QUESTION_GROUP_LOGIC_BRANCHES.filter((branch) =>
                      hasBranch(groupId, branch)
                    ).map((branch) => (
                      <div key={`${groupId}-${branch}`}>
                        <QuestionGroupLogic
                          branch={branch}
                          firstErrorRef={firstErrorRef}
                          groupId={group.id}
                          questionIndex={(groupSourceQuestions[group.id] || []).length}
                          questionList={groupSourceQuestions[group.id] || []}
                          questionLogic={localQuestionLogic}
                          setQuestionLogic={setLocalQuestionLogic}
                        />
                      </div>
                    ))}
                  </div>
                )}
                {isExpanded && hasBranch(groupId, 'skip') && hasBranch(groupId, 'disqualify') && (
                  <div className={styles['modal-question-group-logic-note']}>
                    Disqualification only applies when this question group is displayed.
                  </div>
                )}
                {isExpanded && hasGroupLogicErrors(groupId) && (
                  <div className={styles['modal-question-group-logic-error']}>
                    <ErrorText text="The logic cannot be applied because there is an error." />
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </Layout.Flex.Column>
    </Modal>
  );
};

ModalQuestionGroupLogic.propTypes = {
  onClose: PropTypes.func.isRequired,
  questionGroups: PropTypes.array.isRequired,
  questionLogic: PropTypes.object.isRequired,
  questions: PropTypes.array.isRequired,
  setQuestionLogic: PropTypes.func.isRequired,
};

export default ModalQuestionGroupLogic;
